import React, { useState, useEffect, useRef } from "react";
function FretVizPrivacyPolicy() {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const privacyPolicyRef = useRef(null);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash === "#fretviz-privacy-policy") {
        // Expand the privacy policy
        setIsCollapsed(false);
        // Scroll to the privacy policy section
        setTimeout(() => {
          if (privacyPolicyRef.current) {
            privacyPolicyRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 100);
      }
    };

    // Check hash on mount
    handleHashChange();

    // Listen for hash changes
    window.addEventListener("hashchange", handleHashChange);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  return (
    <div id="fretviz-privacy-policy" ref={privacyPolicyRef}>
      <div class="header" style={{ cursor: "pointer" }} onClick={() => setIsCollapsed(!isCollapsed)}>
        <h1>PRIVACY POLICY {isCollapsed ? "▼" : "▲"}</h1>
        <div class="last-updated">Last updated May 29, 2025</div>
      </div>

      {!isCollapsed && (
        <>
          <div class="section">
        <p>
          This Privacy Notice for Woolworth Software, LLC ("
          <span class="emphasis">we</span>," "<span class="emphasis">us</span>,"
          or "<span class="emphasis">our</span>"), describes how and why we
          might access, collect, store, use, and/or share ("
          <span class="emphasis">process</span>") your personal information when
          you use our services ("<span class="emphasis">Services</span>"),
          including when you:
        </p>

        <ul>
          <li>
            Download and use our mobile application (FretViz), or any other
            application of ours that links to this Privacy Notice
          </li>
          <li>
            Use FretViz. An application for guitar players to help visualize
            notes, chords, and scales on a guitar fretboard.
          </li>
          <li>
            Engage with us in other related ways, including any sales,
            marketing, or events
          </li>
        </ul>

        <div class="product-feature">
          <p>
            <span class="emphasis">Questions or concerns?</span> Reading this
            Privacy Notice will help you understand your privacy rights and
            choices. We are responsible for making decisions about how your
            personal information is processed. If you do not agree with our
            policies and practices, please do not use our Services. If you still
            have any questions or concerns, please contact us at{" "}
            <a href="mailto:support@woolworthsoftware.com">
              support@woolworthsoftware.com
            </a>
          </p>
        </div>
      </div>

      <div class="summary">
        <h2>SUMMARY OF KEY POINTS</h2>
        <p>
          <span class="emphasis italic-emphasis">
            This summary provides key points from our Privacy Notice, but you
            can find out more details about any of these topics by clicking the
            link following each key point or by using our{" "}
            <a href="#toc" class="toc-link">
              table of contents
            </a>{" "}
            below to find the section you are looking for.
          </span>
        </p>
        <p>
          <span class="emphasis">What personal information do we process?</span>{" "}
          When you visit, use, or navigate our Services, we may process personal
          information depending on how you interact with us and the Services,
          the choices you make, and the products and features you use. Learn
          more about{" "}
          <a href="#personalinfo">personal information you disclose to us</a>.
        </p>
        <p>
          <span class="emphasis">
            Do we process any sensitive personal information?
          </span>{" "}
          Some of the information may be considered "special" or "sensitive" in
          certain jurisdictions, for example your racial or ethnic origins,
          sexual orientation, and religious beliefs. We do not process sensitive
          personal information.
        </p>
        <p>
          <span class="emphasis">
            Do we collect any information from third parties?
          </span>{" "}
          We do not collect any information from third parties.
        </p>
        <p>
          <span class="emphasis">How do we process your information?</span> We
          process your information to provide, improve, and administer our
          Services, communicate with you, for security and fraud prevention, and
          to comply with law. We may also process your information for other
          purposes with your consent. We process your information only when we
          have a valid legal reason to do so. Learn more about{" "}
          <a href="#infouse">how we process your information</a>.
        </p>
        <p>
          <span class="emphasis">
            In what situations and with which parties do we share personal
            information?
          </span>{" "}
          We may share information in specific situations and with specific
          third parties. Learn more about{" "}
          <a href="#whoshare">
            when and with whom we share your personal information
          </a>
          .
        </p>
        <p>
          <span class="emphasis">How do we keep your information safe?</span> We
          have adequate organizational and technical processes and procedures in
          place to protect your personal information. However, no electronic
          transmission over the internet or information storage technology can
          be guaranteed to be 100% secure, so we cannot promise or guarantee
          that hackers, cybercriminals, or other unauthorized third parties will
          not be able to defeat our security and improperly collect, access,
          steal, or modify your information. Learn more about{" "}
          <a href="#infosafe">how we keep your information safe</a>.
        </p>
        <p>
          <span class="emphasis">What are your rights?</span> Depending on where
          you are located geographically, the applicable privacy law may mean
          you have certain rights regarding your personal information. Learn
          more about <a href="#privacyrights">your privacy rights</a>.
        </p>
        <p>
          <span class="emphasis">How do you exercise your rights?</span> The
          easiest way to exercise your rights is by submitting a{" "}
          <a
            href="https://www.woolworthsoftware.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            data subject access request
          </a>
          , or by contacting us. We will consider and act upon any request in
          accordance with applicable data protection laws.
        </p>
        <p>
          Want to learn more about what we do with any information we collect?{" "}
          <a href="#toc">Review the Privacy Notice in full</a>.
        </p>
      </div>

      <div class="toc" id="toc">
        <h2>TABLE OF CONTENTS</h2>
        <div class="toc-item">
          <a href="#infocollect" class="toc-link">
            1. WHAT INFORMATION DO WE COLLECT?
          </a>
        </div>
        <div class="toc-item">
          <a href="#infouse" class="toc-link">
            2. HOW DO WE PROCESS YOUR INFORMATION?
          </a>
        </div>
        <div class="toc-item">
          <a href="#legalbases" class="toc-link">
            3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL
            INFORMATION?
          </a>
        </div>
        <div class="toc-item">
          <a href="#whoshare" class="toc-link">
            4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
          </a>
        </div>
        <div class="toc-item">
          <a href="#inforetain" class="toc-link">
            5. HOW LONG DO WE KEEP YOUR INFORMATION?
          </a>
        </div>
        <div class="toc-item">
          <a href="#infosafe" class="toc-link">
            6. HOW DO WE KEEP YOUR INFORMATION SAFE?
          </a>
        </div>
        <div class="toc-item">
          <a href="#privacyrights" class="toc-link">
            7. WHAT ARE YOUR PRIVACY RIGHTS?
          </a>
        </div>
        <div class="toc-item">
          <a href="#DNT" class="toc-link">
            8. CONTROLS FOR DO-NOT-TRACK FEATURES
          </a>
        </div>
        <div class="toc-item">
          <a href="#uslaws" class="toc-link">
            9. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
          </a>
        </div>
        <div class="toc-item">
          <a href="#otherlaws" class="toc-link">
            10. DO OTHER REGIONS HAVE SPECIFIC PRIVACY RIGHTS?
          </a>
        </div>
        <div class="toc-item">
          <a href="#policyupdates" class="toc-link">
            11. DO WE MAKE UPDATES TO THIS NOTICE?
          </a>
        </div>
        <div class="toc-item">
          <a href="#contact" class="toc-link">
            12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
          </a>
        </div>
        <div class="toc-item">
          <a href="#request" class="toc-link">
            13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
            YOU?
          </a>
        </div>
      </div>

      <div class="section" id="infocollect">
        <h2>1. WHAT INFORMATION DO WE COLLECT?</h2>
        <h3>Personal information you disclose to us</h3>
        <p>
          <span class="emphasis italic-emphasis">In Short:</span>{" "}
          <span class="italic-emphasis">
            We collect personal information that you provide to us.
          </span>
        </p>

        <p>
          We collect personal information that you voluntarily provide to us
          when you express an interest in obtaining information about us or our
          products and Services, when you participate in activities on the
          Services, or otherwise when you contact us.
        </p>

        <p>
          <span class="emphasis">Sensitive Information.</span> We do not process
          sensitive information.
        </p>

        <p>
          All personal information that you provide to us must be true,
          complete, and accurate, and you must notify us of any changes to such
          personal information.
        </p>
      </div>

      <div class="section" id="infouse">
        <h2>2. HOW DO WE PROCESS YOUR INFORMATION?</h2>
        <p>
          <span class="emphasis italic-emphasis">In Short:</span>{" "}
          <span class="italic-emphasis">
            We process your information to provide, improve, and administer our
            Services, communicate with you, for security and fraud prevention,
            and to comply with law. We process the personal information for the
            following purposes listed below. We may also process your
            information for other purposes only with your prior explicit
            consent.
          </span>
        </p>

        <p>
          <span class="emphasis">
            We process your personal information for a variety of reasons,
            depending on how you interact with our Services, including:
          </span>
        </p>

        <ul>
          <li>
            <span class="emphasis">
              To save or protect an individual's vital interest.
            </span>{" "}
            We may process your information when necessary to save or protect an
            individual's vital interest, such as to prevent harm.
          </li>
        </ul>
      </div>

      <div class="section" id="legalbases">
        <h2>3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?</h2>
        <p>
          <span class="emphasis italic-emphasis">In Short:</span>{" "}
          <span class="italic-emphasis">
            We only process your personal information when we believe it is
            necessary and we have a valid legal reason (i.e., legal basis) to do
            so under applicable law, like with your consent, to comply with
            laws, to provide you with services to enter into or fulfill our
            contractual obligations, to protect your rights, or to fulfill our
            legitimate business interests.
          </span>
        </p>

        <h3>
          <span class="emphasis">
            If you are located in the EU or UK, this section applies to you.
          </span>
        </h3>

        <p>
          The General Data Protection Regulation (GDPR) and UK GDPR require us
          to explain the valid legal bases we rely on in order to process your
          personal information. As such, we may rely on the following legal
          bases to process your personal information:
        </p>

        <ul>
          <li>
            <span class="emphasis">Consent.</span> We may process your
            information if you have given us permission (i.e., consent) to use
            your personal information for a specific purpose. You can withdraw
            your consent at any time. Learn more about{" "}
            <a href="#withdrawconsent">withdrawing your consent</a>.
          </li>
          <li>
            <span class="emphasis">Legal Obligations.</span> We may process your
            information where we believe it is necessary for compliance with our
            legal obligations, such as to cooperate with a law enforcement body
            or regulatory agency, exercise or defend our legal rights, or
            disclose your information as evidence in litigation in which we are
            involved.
          </li>
          <li>
            <span class="emphasis">Vital Interests.</span> We may process your
            information where we believe it is necessary to protect your vital
            interests or the vital interests of a third party, such as
            situations involving potential threats to the safety of any person.
          </li>
        </ul>

        <h3>
          <span class="emphasis">
            If you are located in Canada, this section applies to you.
          </span>
        </h3>

        <p>
          We may process your information if you have given us specific
          permission (i.e., express consent) to use your personal information
          for a specific purpose, or in situations where your permission can be
          inferred (i.e., implied consent). You can{" "}
          <a href="#withdrawconsent">withdraw your consent</a> at any time.
        </p>

        <p>
          In some exceptional cases, we may be legally permitted under
          applicable law to process your information without your consent,
          including, for example:
        </p>

        <ul>
          <li>
            If collection is clearly in the interests of an individual and
            consent cannot be obtained in a timely way
          </li>
          <li>For investigations and fraud detection and prevention</li>
          <li>For business transactions provided certain conditions are met</li>
          <li>
            If it is contained in a witness statement and the collection is
            necessary to assess, process, or settle an insurance claim
          </li>
          <li>
            For identifying injured, ill, or deceased persons and communicating
            with next of kin
          </li>
          <li>
            If we have reasonable grounds to believe an individual has been, is,
            or may be victim of financial abuse
          </li>
          <li>
            If it is reasonable to expect collection and use with consent would
            compromise the availability or the accuracy of the information and
            the collection is reasonable for purposes related to investigating a
            breach of an agreement or a contravention of the laws of Canada or a
            province
          </li>
          <li>
            If disclosure is required to comply with a subpoena, warrant, court
            order, or rules of the court relating to the production of records
          </li>
          <li>
            If it was produced by an individual in the course of their
            employment, business, or profession and the collection is consistent
            with the purposes for which the information was produced
          </li>
          <li>
            If the collection is solely for journalistic, artistic, or literary
            purposes
          </li>
          <li>
            If the information is publicly available and is specified by the
            regulations
          </li>
          <li>
            We may disclose de-identified information for approved research or
            statistics projects, subject to ethics oversight and confidentiality
            commitments
          </li>
        </ul>
      </div>

      <div class="section" id="whoshare">
        <h2>4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</h2>
        <p>
          <span class="emphasis italic-emphasis">In Short:</span>{" "}
          <span class="italic-emphasis">
            We may share information in specific situations described in this
            section and/or with the following third parties.
          </span>
        </p>

        <p>
          We may need to share your personal information in the following
          situations:
        </p>

        <ul>
          <li>
            <span class="emphasis">Business Transfers.</span> We may share or
            transfer your information in connection with, or during negotiations
            of, any merger, sale of company assets, financing, or acquisition of
            all or a portion of our business to another company.
          </li>
        </ul>
      </div>

      <div class="section" id="inforetain">
        <h2>5. HOW LONG DO WE KEEP YOUR INFORMATION?</h2>
        <p>
          <span class="emphasis italic-emphasis">In Short:</span>{" "}
          <span class="italic-emphasis">
            We keep your information for as long as necessary to fulfill the
            purposes outlined in this Privacy Notice unless otherwise required
            by law.
          </span>
        </p>

        <p>
          We will only keep your personal information for as long as it is
          necessary for the purposes set out in this Privacy Notice, unless a
          longer retention period is required or permitted by law (such as tax,
          accounting, or other legal requirements).
        </p>

        <p>
          When we have no ongoing legitimate business need to process your
          personal information, we will either delete or anonymize such
          information, or, if this is not possible (for example, because your
          personal information has been stored in backup archives), then we will
          securely store your personal information and isolate it from any
          further processing until deletion is possible.
        </p>
      </div>

      <div class="section" id="infosafe">
        <h2>6. HOW DO WE KEEP YOUR INFORMATION SAFE?</h2>
        <p>
          <span class="emphasis italic-emphasis">In Short:</span>{" "}
          <span class="italic-emphasis">
            We aim to protect your personal information through a system of
            organizational and technical security measures.
          </span>
        </p>

        <p>
          We have implemented appropriate and reasonable technical and
          organizational security measures designed to protect the security of
          any personal information we process. However, despite our safeguards
          and efforts to secure your information, no electronic transmission
          over the Internet or information storage technology can be guaranteed
          to be 100% secure, so we cannot promise or guarantee that hackers,
          cybercriminals, or other unauthorized third parties will not be able
          to defeat our security and improperly collect, access, steal, or
          modify your information. Although we will do our best to protect your
          personal information, transmission of personal information to and from
          our Services is at your own risk. You should only access the Services
          within a secure environment.
        </p>
      </div>

      <div class="section" id="privacyrights">
        <h2>7. WHAT ARE YOUR PRIVACY RIGHTS?</h2>
        <p>
          <span class="emphasis italic-emphasis">In Short:</span>{" "}
          <span class="italic-emphasis">
            Depending on your state of residence in the US or in some regions,
            such as the European Economic Area (EEA), United Kingdom (UK),
            Switzerland, and Canada, you have rights that allow you greater
            access to and control over your personal information. You may
            review, change, or terminate your account at any time, depending on
            your country, province, or state of residence.
          </span>
        </p>

        <p>
          In some regions (like the EEA, UK, Switzerland, and Canada), you have
          certain rights under applicable data protection laws. These may
          include the right (i) to request access and obtain a copy of your
          personal information, (ii) to request rectification or erasure; (iii)
          to restrict the processing of your personal information; (iv) if
          applicable, to data portability; and (v) not to be subject to
          automated decision-making. If a decision that produces legal or
          similarly significant effects is made solely by automated means, we
          will inform you, explain the main factors, and offer a simple way to
          request human review. In certain circumstances, you may also have the
          right to object to the processing of your personal information. You
          can make such a request by contacting us by using the contact details
          provided in the section "
          <a href="#contact">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</a>"
          below.
        </p>

        <p>
          We will consider and act upon any request in accordance with
          applicable data protection laws.
        </p>

        <p>
          If you are located in the EEA or UK and you believe we are unlawfully
          processing your personal information, you also have the right to
          complain to your{" "}
          <a
            href="https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm"
            target="_blank"
            rel="noopener noreferrer"
          >
            Member State data protection authority
          </a>{" "}
          or{" "}
          <a
            href="https://ico.org.uk/make-a-complaint/data-protection-complaints/data-protection-complaints/"
            target="_blank"
            rel="noopener noreferrer"
          >
            UK data protection authority
          </a>
          .
        </p>

        <p>
          If you are located in Switzerland, you may contact the{" "}
          <a
            href="https://www.edoeb.admin.ch/edoeb/en/home.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            Federal Data Protection and Information Commissioner
          </a>
          .
        </p>

        <h3 id="withdrawconsent">Withdrawing your consent</h3>
        <p>
          If we are relying on your consent to process your personal
          information, which may be express and/or implied consent depending on
          the applicable law, you have the right to withdraw your consent at any
          time. You can withdraw your consent at any time by contacting us by
          using the contact details provided in the section "
          <a href="#contact">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</a>"
          below.
        </p>

        <p>
          However, please note that this will not affect the lawfulness of the
          processing before its withdrawal nor, when applicable law allows, will
          it affect the processing of your personal information conducted in
          reliance on lawful processing grounds other than consent.
        </p>

        <p>
          If you have questions or comments about your privacy rights, you may
          email us at{" "}
          <a href="mailto:support@woolworthsoftware.com">
            support@woolworthsoftware.com
          </a>
        </p>
      </div>

      <div class="section" id="DNT">
        <h2>8. CONTROLS FOR DO-NOT-TRACK FEATURES</h2>
        <p>
          Most web browsers and some mobile operating systems and mobile
          applications include a Do-Not-Track ("DNT") feature or setting you can
          activate to signal your privacy preference not to have data about your
          online browsing activities monitored and collected. At this stage, no
          uniform technology standard for recognizing and implementing DNT
          signals has been finalized. As such, we do not currently respond to
          DNT browser signals or any other mechanism that automatically
          communicates your choice not to be tracked online. If a standard for
          online tracking is adopted that we must follow in the future, we will
          inform you about that practice in a revised version of this Privacy
          Notice.
        </p>

        <p>
          California law requires us to let you know how we respond to web
          browser DNT signals. Because there currently is not an industry or
          legal standard for recognizing or honoring DNT signals, we do not
          respond to them at this time.
        </p>
      </div>

      <div class="section" id="uslaws">
        <h2>9. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</h2>
        <p>
          <span class="emphasis italic-emphasis">In Short:</span>{" "}
          <span class="italic-emphasis">
            If you are a resident of California, Colorado, Connecticut,
            Delaware, Florida, Indiana, Iowa, Kentucky, Maryland, Minnesota,
            Montana, Nebraska, New Hampshire, New Jersey, Oregon, Rhode Island,
            Tennessee, Texas, Utah, or Virginia, you may have the right to
            request access to and receive details about the personal information
            we maintain about you and how we have processed it, correct
            inaccuracies, get a copy of, or delete your personal information.
            You may also have the right to withdraw your consent to our
            processing of your personal information. These rights may be limited
            in some circumstances by applicable law. More information is
            provided below.
          </span>
        </p>

        <h3>Categories of Personal Information We Collect</h3>
        <p>
          The table below shows the categories of personal information we have
          collected in the past twelve (12) months. The table includes
          illustrative examples of each category and does not reflect the
          personal information we collect from you. For a comprehensive
          inventory of all personal information we process, please refer to the
          section "<a href="#infocollect">WHAT INFORMATION DO WE COLLECT?</a>"
        </p>

        <table class="privacy-table">
          <thead>
            <tr>
              <th>Category</th>
              <th>Examples</th>
              <th>Collected</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>A. Identifiers</td>
              <td>
                Contact details, such as real name, alias, postal address,
                telephone or mobile contact number, unique personal identifier,
                online identifier, Internet Protocol address, email address, and
                account name
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>
                B. Personal information as defined in the California Customer
                Records statute
              </td>
              <td>
                Name, contact information, education, employment, employment
                history, and financial information
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>
                C. Protected classification characteristics under state or
                federal law
              </td>
              <td>
                Gender, age, date of birth, race and ethnicity, national origin,
                marital status, and other demographic data
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>D. Commercial information</td>
              <td>
                Transaction information, purchase history, financial details,
                and payment information
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>E. Biometric information</td>
              <td>Fingerprints and voiceprints</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>F. Internet or other similar network activity</td>
              <td>
                Browsing history, search history, online behavior, interest
                data, and interactions with our and other websites,
                applications, systems, and advertisements
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>G. Geolocation data</td>
              <td>Device location</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>H. Audio, electronic, sensory, or similar information</td>
              <td>
                Images and audio, video or call recordings created in connection
                with our business activities
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>I. Professional or employment-related information</td>
              <td>
                Business contact details in order to provide you our Services at
                a business level or job title, work history, and professional
                qualifications if you apply for a job with us
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>J. Education Information</td>
              <td>Student records and directory information</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>K. Inferences drawn from collected personal information</td>
              <td>
                Inferences drawn from any of the collected personal information
                listed above to create a profile or summary about, for example,
                an individual's preferences and characteristics
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>L. Sensitive personal Information</td>
              <td>&nbsp;</td>
              <td>NO</td>
            </tr>
          </tbody>
        </table>

        <p>
          We may also collect other personal information outside of these
          categories through instances where you interact with us in person,
          online, or by phone or mail in the context of:
        </p>

        <ul>
          <li>Receiving help through our customer support channels;</li>
          <li>Participation in customer surveys or contests; and</li>
          <li>
            Facilitation in the delivery of our Services and to respond to your
            inquiries.
          </li>
        </ul>

        <h3>Sources of Personal Information</h3>
        <p>
          Learn more about the sources of personal information we collect in "
          <a href="#infocollect">WHAT INFORMATION DO WE COLLECT?</a>"
        </p>

        <h3>How We Use and Share Personal Information</h3>
        <p>
          Learn more about how we use your personal information in the section,
          "<a href="#infouse">HOW DO WE PROCESS YOUR INFORMATION?</a>"
        </p>

        <h3>Will your information be shared with anyone else?</h3>
        <p>
          We may disclose your personal information with our service providers
          pursuant to a written contract between us and each service provider.
          Learn more about how we disclose personal information in the section,
          "
          <a href="#whoshare">
            WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
          </a>
          "
        </p>

        <p>
          We may use your personal information for our own business purposes,
          such as for undertaking internal research for technological
          development and demonstration. This is not considered to be "selling"
          of your personal information.
        </p>

        <p>
          We have not disclosed, sold, or shared any personal information to
          third parties for a business or commercial purpose in the preceding
          twelve (12) months. We will not sell or share personal information in
          the future belonging to website visitors, users, and other consumers.
        </p>

        <h3>Your Rights</h3>
        <p>
          You have rights under certain US state data protection laws. However,
          these rights are not absolute, and in certain cases, we may decline
          your request as permitted by law. These rights include:
        </p>

        <ul>
          <li>
            <span class="emphasis">Right to know</span> whether or not we are
            processing your personal data
          </li>
          <li>
            <span class="emphasis">Right to access</span> your personal data
          </li>
          <li>
            <span class="emphasis">Right to correct</span> inaccuracies in your
            personal data
          </li>
          <li>
            <span class="emphasis">Right to request</span> the deletion of your
            personal data
          </li>
          <li>
            <span class="emphasis">Right to obtain a copy</span> of the personal
            data you previously shared with us
          </li>
          <li>
            <span class="emphasis">Right to non-discrimination</span> for
            exercising your rights
          </li>
          <li>
            <span class="emphasis">Right to opt out</span> of the processing of
            your personal data if it is used for targeted advertising (or
            sharing as defined under California's privacy law), the sale of
            personal data, or profiling in furtherance of decisions that produce
            legal or similarly significant effects ("profiling")
          </li>
        </ul>

        <p>
          Depending upon the state where you live, you may also have the
          following rights:
        </p>

        <ul>
          <li>
            Right to access the categories of personal data being processed (as
            permitted by applicable law, including the privacy law in Minnesota)
          </li>
          <li>
            Right to obtain a list of the categories of third parties to which
            we have disclosed personal data (as permitted by applicable law,
            including the privacy law in California, Delaware, and Maryland)
          </li>
          <li>
            Right to obtain a list of specific third parties to which we have
            disclosed personal data (as permitted by applicable law, including
            the privacy law in Minnesota and Oregon)
          </li>
          <li>
            Right to review, understand, question, and correct how personal data
            has been profiled (as permitted by applicable law, including the
            privacy law in Minnesota)
          </li>
          <li>
            Right to limit use and disclosure of sensitive personal data (as
            permitted by applicable law, including the privacy law in
            California)
          </li>
          <li>
            Right to opt out of the collection of sensitive data and personal
            data collected through the operation of a voice or facial
            recognition feature (as permitted by applicable law, including the
            privacy law in Florida)
          </li>
        </ul>

        <h3>How to Exercise Your Rights</h3>
        <p>
          To exercise these rights, you can contact us by submitting a{" "}
          <a
            href="https://www.woolworthsoftware.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            data subject access request
          </a>
          , by emailing us at{" "}
          <a href="mailto:support@woolworthsoftware.com">
            support@woolworthsoftware.com
          </a>
          , or by referring to the contact details at the bottom of this
          document.
        </p>

        <p>
          Under certain US state data protection laws, you can designate an
          authorized agent to make a request on your behalf. We may deny a
          request from an authorized agent that does not submit proof that they
          have been validly authorized to act on your behalf in accordance with
          applicable laws.
        </p>

        <h3>Request Verification</h3>
        <p>
          Upon receiving your request, we will need to verify your identity to
          determine you are the same person about whom we have the information
          in our system. We will only use personal information provided in your
          request to verify your identity or authority to make the request.
          However, if we cannot verify your identity from the information
          already maintained by us, we may request that you provide additional
          information for the purposes of verifying your identity and for
          security or fraud-prevention purposes.
        </p>

        <p>
          If you submit the request through an authorized agent, we may need to
          collect additional information to verify your identity before
          processing your request and the agent will need to provide a written
          and signed permission from you to submit such request on your behalf.
        </p>

        <h3>Appeals</h3>
        <p>
          Under certain US state data protection laws, if we decline to take
          action regarding your request, you may appeal our decision by emailing
          us at{" "}
          <a href="mailto:support@woolworthsoftware.com">
            support@woolworthsoftware.com
          </a>
          . We will inform you in writing of any action taken or not taken in
          response to the appeal, including a written explanation of the reasons
          for the decisions. If your appeal is denied, you may submit a
          complaint to your state attorney general.
        </p>

        <h3>California "Shine The Light" Law</h3>
        <p>
          California Civil Code Section 1798.83, also known as the "Shine The
          Light" law, permits our users who are California residents to request
          and obtain from us, once a year and free of charge, information about
          categories of personal information (if any) we disclosed to third
          parties for direct marketing purposes and the names and addresses of
          all third parties with which we shared personal information in the
          immediately preceding calendar year. If you are a California resident
          and would like to make such a request, please submit your request in
          writing to us by using the contact details provided in the section "
          <a href="#contact">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</a>"
        </p>
      </div>

      <div class="section" id="otherlaws">
        <h2>10. DO OTHER REGIONS HAVE SPECIFIC PRIVACY RIGHTS?</h2>
        <p>
          <span class="emphasis italic-emphasis">In Short:</span>{" "}
          <span class="italic-emphasis">
            You may have additional rights based on the country you reside in.
          </span>
        </p>

        <h3>Australia and New Zealand</h3>
        <p>
          We collect and process your personal information under the obligations
          and conditions set by Australia's Privacy Act 1988 and New Zealand's
          Privacy Act 2020 (Privacy Act).
        </p>

        <p>
          This Privacy Notice satisfies the notice requirements defined in both
          Privacy Acts, in particular: what personal information we collect from
          you, from which sources, for which purposes, and other recipients of
          your personal information.
        </p>

        <p>
          If you do not wish to provide the personal information necessary to
          fulfill their applicable purpose, it may affect our ability to provide
          our services, in particular:
        </p>

        <ul>
          <li>offer you the products or services that you want</li>
          <li>respond to or help with your requests</li>
        </ul>

        <p>
          At any time, you have the right to request access to or correction of
          your personal information. You can make such a request by contacting
          us by using the contact details provided in the section "
          <a href="#request">
            HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
          </a>
          "
        </p>

        <p>
          If you believe we are unlawfully processing your personal information,
          you have the right to submit a complaint about a breach of the
          Australian Privacy Principles to the{" "}
          <a
            href="https://www.oaic.gov.au/privacy/privacy-complaints/lodge-a-privacy-complaint-with-us"
            target="_blank"
            rel="noopener noreferrer"
          >
            Office of the Australian Information Commissioner
          </a>{" "}
          and a breach of New Zealand's Privacy Principles to the{" "}
          <a
            href="https://www.privacy.org.nz/your-rights/making-a-complaint/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Office of New Zealand Privacy Commissioner
          </a>
          .
        </p>

        <h3>Republic of South Africa</h3>
        <p>
          At any time, you have the right to request access to or correction of
          your personal information. You can make such a request by contacting
          us by using the contact details provided in the section "
          <a href="#request">
            HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
          </a>
          "
        </p>

        <p>
          If you are unsatisfied with the manner in which we address any
          complaint with regard to our processing of personal information, you
          can contact the office of the regulator, the details of which are:
        </p>

        <p>
          <a
            href="https://inforegulator.org.za/"
            target="_blank"
            rel="noopener noreferrer"
          >
            The Information Regulator (South Africa)
          </a>
          <br />
          General enquiries:{" "}
          <a
            href="mailto:enquiries@inforegulator.org.za"
            target="_blank"
            rel="noopener noreferrer"
          >
            enquiries@inforegulator.org.za
          </a>
          <br />
          Complaints (complete POPIA/PAIA form 5):{" "}
          <a
            href="mailto:PAIAComplaints@inforegulator.org.za"
            target="_blank"
            rel="noopener noreferrer"
          >
            PAIAComplaints@inforegulator.org.za
          </a>{" "}
          &{" "}
          <a
            href="mailto:POPIAComplaints@inforegulator.org.za"
            target="_blank"
            rel="noopener noreferrer"
          >
            POPIAComplaints@inforegulator.org.za
          </a>
        </p>
      </div>

      <div class="section" id="policyupdates">
        <h2>11. DO WE MAKE UPDATES TO THIS NOTICE?</h2>
        <p>
          <span class="emphasis italic-emphasis">In Short:</span>
          <span class="italic-emphasis">
            Yes, we will update this notice as necessary to stay compliant with
            relevant laws.
          </span>
        </p>

        <p>
          We may update this Privacy Notice from time to time. The updated
          version will be indicated by an updated "Revised" date at the top of
          this Privacy Notice. If we make material changes to this Privacy
          Notice, we may notify you either by prominently posting a notice of
          such changes or by directly sending you a notification. We encourage
          you to review this Privacy Notice frequently to be informed of how we
          are protecting your information.
        </p>
      </div>

      <div class="section" id="contact">
        <h2>12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</h2>
        <p>
          If you have questions or comments about this notice, you may email us
          at{" "}
          <a href="mailto:support@woolworthsoftware.com">
            support@woolworthsoftware.com
          </a>{" "}
          or contact us by post at:
        </p>

        <div class="product-feature">
          <p>
            Woolworth Software, LLC
            <br />
            17608 Woolworth Avenue
            <br />
            Omaha, NE 68130
            <br />
            United States
          </p>
        </div>

        <div class="section" id="request">
          <h2>
            13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
            YOU?
          </h2>
          <p>
            Based on the applicable laws of your country or state of residence
            in the US, you may have the right to request access to the personal
            information we collect from you, details about how we have processed
            it, correct inaccuracies, or delete your personal information. You
            may also have the right to withdraw your consent to our processing
            of your personal information. These rights may be limited in some
            circumstances by applicable law. To request to review, update, or
            delete your personal information, please fill out and submit a{" "}
            <a
              href="https://www.woolworthsoftware.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              data subject access request
            </a>
            .
          </p>
        </div>
      </div>
        </>
      )}
    </div>
  );
}

export default FretVizPrivacyPolicy;
